import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Terminal, ShieldAlert } from "lucide-react";

type GroupRow = {
  id: string;
  name: string | null;
  group_jid: string;
  is_dev_console: boolean;
};

type AuditRow = {
  group_id: string;
  created_at: string;
  reason: string | null;
};

const reasonSchema = z
  .string()
  .trim()
  .min(5, { message: "Motivo obrigatório (mínimo 5 caracteres)." })
  .max(500, { message: "Motivo deve ter no máximo 500 caracteres." });

function errMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "string") return e;
  if (e && typeof e === "object" && "message" in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  try {
    return JSON.stringify(e);
  } catch {
    return "Erro desconhecido";
  }
}

export default function DevConsoleGroups() {
  const { user } = useAuth();
  const role = user?.role;
  const qc = useQueryClient();

  const [editing, setEditing] = useState<GroupRow | null>(null);
  const [pendingEnabled, setPendingEnabled] = useState<boolean>(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);

  const { data: groups, isLoading: loadingGroups } = useQuery({
    queryKey: ["admin-dev-console-groups"],
    queryFn: async (): Promise<GroupRow[]> => {
      const { data, error } = await supabase
        .from("syra_groups")
        .select("id, name, group_jid, is_dev_console")
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as GroupRow[];
    },
  });

  const { data: enabledCommandsCount = 0 } = useQuery({
    queryKey: ["admin-dev-console-commands-count"],
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from("operations_console_commands")
        .select("*", { count: "exact", head: true })
        .eq("enabled", true);
      if (error) throw error;
      return count ?? 0;
    },
  });

  const { data: auditByGroup } = useQuery({
    queryKey: ["admin-dev-console-audit"],
    queryFn: async (): Promise<Record<string, AuditRow>> => {
      const { data, error } = await supabase
        .from("audit_logs")
        .select("details, created_at")
        .eq("action", "admin-dev-console-toggle")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      const map: Record<string, AuditRow> = {};
      for (const row of (data ?? []) as Array<{ details: any; created_at: string }>) {
        const gid = row.details?.group_id as string | undefined;
        if (!gid || map[gid]) continue;
        map[gid] = {
          group_id: gid,
          created_at: row.created_at,
          reason: (row.details?.reason as string | undefined) ?? null,
        };
      }
      return map;
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async (input: { group: GroupRow; nextValue: boolean; reason: string }) => {
      const actorId = user?.id ?? null;
      const { error: upErr } = await supabase
        .from("syra_groups")
        .update({ is_dev_console: input.nextValue })
        .eq("id", input.group.id);
      if (upErr) throw upErr;

      const { error: auditErr } = await supabase.from("audit_logs").insert([{
        action: "admin-dev-console-toggle",
        resource_type: "syra_groups",
        resource_id: input.group.id,
        user_id: actorId ?? undefined,
        details: {
          group_id: input.group.id,
          group_jid: input.group.group_jid,
          group_name: input.group.name,
          from: input.group.is_dev_console,
          to: input.nextValue,
          reason: input.reason,
          actor_id: actorId,
        },
      }]);
      if (auditErr) throw auditErr;
      return { ...input };
    },
    onSuccess: ({ group, nextValue }) => {
      qc.invalidateQueries({ queryKey: ["admin-dev-console-groups"] });
      qc.invalidateQueries({ queryKey: ["admin-dev-console-audit"] });
      toast.success(
        `Dev Console ${nextValue ? "habilitado" : "desabilitado"} em ${group.name ?? group.group_jid}`,
      );
      setEditing(null);
      setReason("");
      setReasonError(null);
    },
    onError: (e: unknown) => {
      toast.error(`Falha ao atualizar: ${errMsg(e)}`);
    },
  });

  const stats = useMemo(() => {
    const total = groups?.length ?? 0;
    const optedIn = groups?.filter((g) => g.is_dev_console).length ?? 0;
    return { total, optedIn };
  }, [groups]);

  if (role !== "owner" && role !== "admin" && role !== "superadmin") {
    return <Navigate to="/dashboard" replace />;
  }

  const handleToggleClick = (group: GroupRow) => {
    setEditing(group);
    setPendingEnabled(!group.is_dev_console);
    setReason("");
    setReasonError(null);
  };

  const handleSubmit = () => {
    const parsed = reasonSchema.safeParse(reason);
    if (!parsed.success) {
      setReasonError(parsed.error.errors[0]?.message ?? "Motivo inválido.");
      return;
    }
    if (!editing) return;
    toggleMutation.mutate({
      group: editing,
      nextValue: pendingEnabled,
      reason: parsed.data,
    });
  };

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <div className="flex items-center gap-2">
          <Terminal className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold font-headline">Dev Console — Grupos opt-in</h1>
        </div>
        <p className="text-sm text-muted-foreground max-w-3xl">
          Habilite o Operations Dev Console em grupos específicos. Apenas grupos
          marcados como <code className="text-xs">is_dev_console = true</code> aceitam
          comandos administrativos via WhatsApp (endpoint <code className="text-xs">/syra-dev</code>).
          Cada alteração é registrada em audit logs com motivo obrigatório.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Grupos totais</CardDescription>
            <CardTitle className="text-3xl">{stats.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Opt-in Dev Console</CardDescription>
            <CardTitle className="text-3xl">{stats.optedIn}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Comandos habilitados (global)</CardDescription>
            <CardTitle className="text-3xl">{enabledCommandsCount}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Grupos</CardTitle>
          <CardDescription>
            Toggle por grupo. A ação requer motivo (mínimo 5 caracteres) e é
            registrada em <code className="text-xs">audit_logs</code>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingGroups ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : !groups || groups.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
              <ShieldAlert className="h-8 w-8" />
              <p className="text-sm">Nenhum grupo encontrado em syra_groups.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Grupo</TableHead>
                  <TableHead className="w-[140px]">Dev Console</TableHead>
                  <TableHead className="w-[200px]">Última alteração</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((g) => {
                  const audit = auditByGroup?.[g.id];
                  return (
                    <TableRow key={g.id}>
                      <TableCell>
                        <div className="font-medium">{g.name ?? "(sem nome)"}</div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {g.group_jid}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={g.is_dev_console}
                            onCheckedChange={() => handleToggleClick(g)}
                            disabled={toggleMutation.isPending}
                            aria-label={`Toggle Dev Console para ${g.name ?? g.group_jid}`}
                          />
                          <Badge variant={g.is_dev_console ? "default" : "secondary"}>
                            {g.is_dev_console ? "ON" : "OFF"}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {audit ? (
                          <div>
                            <div>{new Date(audit.created_at).toLocaleString("pt-BR")}</div>
                            {audit.reason && (
                              <div className="truncate max-w-[180px]" title={audit.reason}>
                                {audit.reason}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="italic">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!editing}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(null);
            setReason("");
            setReasonError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingEnabled ? "Habilitar" : "Desabilitar"} Dev Console
            </DialogTitle>
            <DialogDescription>
              Grupo:{" "}
              <strong>{editing?.name ?? editing?.group_jid}</strong>. Informe o motivo
              da alteração — será registrado em audit logs.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="reason">Motivo (mínimo 5 caracteres)</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (reasonError) setReasonError(null);
              }}
              placeholder="Ex: Habilitando para QA do comando /health no grupo de testes."
              rows={3}
              maxLength={500}
              aria-invalid={!!reasonError}
            />
            {reasonError && (
              <p className="text-sm text-destructive" role="alert">
                {reasonError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditing(null)}
              disabled={toggleMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={toggleMutation.isPending}
            >
              {toggleMutation.isPending ? "Salvando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
